import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";

import styles from "./moonshot.module.scss";

const Moonshot = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const smallDevice = windowWidth <= 990;
  const largeDevice = windowWidth > 990;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    setWindowWidth(window.innerWidth);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#moonshotWrapper",
        scrub: true,
        end: "+=750",
      },
    });

    const moonTimeline = gsap.timeline();
    moonTimeline.from("#moon", {
      y: 350,
      duration: 90,
      opacity: 0.8,
      delay: 4,
    });

    const textTimeline = gsap.timeline();
    textTimeline.from("#textContent", {
      y: 500,
      duration: 95,
      opacity: 0.5,
      delay: 5,
    });

    mainTimeline.add(moonTimeline, 12);
    mainTimeline.add(textTimeline, 24);
  }, []);

  return (
    <section className={styles.moonshotWrapper} id="moonshotWrapper">
      <div className={styles.sceneSection}>
        <div className={styles.moonWrapper}>
          <img
            className={styles.moon}
            id="moon"
            src={"/images/moonshot/moon.png"}
            alt=""
          />
          <div className={styles.textContent} id="textContent">
            <p className={styles.theMoonshot}>
              <span>The</span>
              <span>Moonshot!</span>
            </p>
            <p className={styles.staffFavorite}>Our Staff Favorite</p>
            <a href="https://campaigns-grammarly.cleverstory.io/the-productivity-shift-ROI/the-productivity-shift" target="_blank">
              <img
                className={styles.moonshotLogo}
                src={"/images/moonshot/oneAdvanced.png"}
              />
            </a>
          </div>
        </div>
        <img
          className={styles.terrainImg}
          src={
            largeDevice
              ? "/images/moonshot/terrain_desktop.png"
              : "/images/moonshot/terrain_mobile.png"
          }
        />
        <img
          className={styles.astronaut}
          src={"/images/moonshot/astronaut.png"}
          alt=""
        />
      </div>

      <div className={styles.contentSection}>
        {largeDevice &&
          <div className={styles.imageSection}>
            <img
              className={styles.websiteImg} src="/images/moonshot/flipImg.png"
            />

            <img
              className={styles.builtIn3days}
              src={"/images/moonshot/builtIn3days.png"}
              alt=""
            />
            <img
              className={styles.builtWithCleverstory}
              src={"/images/moonshot/builtWithCleverstory.png"}
              alt=""
            />
          </div>
        }
        <div className={styles.textSection}>
          <p className={styles.timeline}>DECEMBER, 2025</p>
          <h2 className={styles.heading}>
            How oneAdvanced Rebuilt Reports the Way Buyers Engage Today
          </h2>

          <div className={styles.QnA}>
            {smallDevice &&
              <div className={styles.imageSection}>
                <img 
                  className={styles.websiteImg} src="/images/moonshot/flipImg.png"
                />

                <img
                  className={styles.builtIn3days}
                  src={"/images/moonshot/builtIn3days.png"}
                  alt=""
                />
                <img
                  className={styles.builtWithCleverstory}
                  src={"/images/moonshot/builtWithCleverstory.png"}
                  alt=""
                />
              </div>
            }
            <div className={styles.answer}>
              <span className={styles.paragraph}>
                oneAdvanced builds grounded, simple & helpful content for people who rarely get a quiet minute. From sector reports that inform to case studies that solve rooted problems, they go the whole nine yards.
              </span>

              <span className={styles.paragraph}>
                Here's the thing: their content was trapped in PDFs. It's like serving an 11-course meal in a to-go container. But how did people actually consume it? This question revealed something important:
              </span>

              <ul className={styles.insightsList}>
                <li className={styles.listItem}>
                  A healthcare admin opening a report between site visits was trying to get straight to the part relevant to today's caseload.
                </li>
                <li className={styles.listItem}>
                  A retail manager looking at a case study during a short break wanted the section that answered their immediate operational question.
                </li>
                <li className={styles.listItem}>
                  A finance leader scrolling through sector insights on the commute was searching for one useful takeaway, not pages of context.
                </li>
              </ul>

              <span className={styles.paragraph}>
                Traditional formats, long PDFs, linear pages, and content locked behind forms simply weren't built for these moments. They asked busy professionals to adapt to the content, rather than the other way around.
              </span>

              <span className={styles.paragraph}>
                Different jobs. Different pressures. Same reading pattern: people were searching for the part that mattered to them at that moment. That's when oneAdvanced asked a better question: What if content adapted to the reader, instead of making the reader adapt to the content?
              </span>

              <span className={styles.paragraph}>
                To make this possible, they needed to create an experience that let each person choose their own path, reach relevance instantly, and move without friction.
              </span>

              <span className={styles.paragraph}>
                With Cleverstory, they brought this vision to life -  they transformed their reports into guided chapters that readers could explore freely. People could jump straight to what matters most. And instead of demanding an email upfront, they gave readers the freedom to explore first.
              </span>

              <span className={styles.paragraph}>
                Now oneAdvanced runs personalized experiences for each core audience, healthcare, retail, and logistics using the personalisation feature. This shift changed what oneAdvanced could see. Which sections actually resonate with the reader, which insights get revisited, and where they dropped off.
              </span>

              <span className={styles.paragraph}>
                When someone explores an oneAdvanced experience today, they're not just consuming information. They're seeing their actual workday reflected the chaos, the priorities, the moments that matter. And when people feel understood, the conversation that follows is completely different.
              </span>
            </div>
            <div className={styles.liveExperienceLink}>
              <a href="https://campaigns-oneadvanced.cleverstory.io/one-advance/disenfranchised" target="_blank">
                Live Experience
                <img className={styles.shortArrow} src={"/images/moonshot/shortArrow.png"} alt=""
              />
              </a>
            </div>
            {smallDevice &&
              <img
                className={styles.astronautImage}
                src={"/images/astronaut.png"}
                alt=""
              />
            }
          </div>
        </div>
      </div>
    </section>
  );
};

export default Moonshot;
